/**
 * useOptimizer -- Zustand store for portfolio optimization engine.
 */
import { create } from 'zustand'
import {
  optimizeMarkowitz, optimizeRiskParity, getEfficientFrontier,
  getRebalanceTrades, getCurrentVsOptimal,
} from '@/lib/api'
import type {
  MarkowitzResult, RiskParityResult, FrontierResult,
  RebalanceResult, CurrentVsOptimal,
} from '@/lib/types'

type Strategy = 'max_sharpe' | 'min_variance' | 'risk_parity'

interface OptimizerState {
  markowitz: MarkowitzResult | null
  riskParity: RiskParityResult | null
  frontier: FrontierResult | null
  rebalance: RebalanceResult | null
  comparison: CurrentVsOptimal | null
  strategy: Strategy
  tickers: string[]
  loading: boolean
  error: string | null
  setStrategy: (s: Strategy) => void
  setTickers: (t: string[]) => void
  runOptimize: (tickers: string[]) => Promise<void>
  runFrontier: (tickers: string[]) => Promise<void>
  runRebalance: (holdings: Record<string, number>, weights: Record<string, number>) => Promise<void>
  fetchComparison: () => Promise<void>
  clear: () => void
}

export const useOptimizer = create<OptimizerState>()((set, get) => ({
  markowitz: null,
  riskParity: null,
  frontier: null,
  rebalance: null,
  comparison: null,
  strategy: 'max_sharpe',
  tickers: ['AAPL', 'MSFT', 'NVDA', 'GOOGL'],
  loading: false,
  error: null,

  setStrategy: (s) => set({ strategy: s }),
  setTickers: (t) => set({ tickers: t }),

  runOptimize: async (tickers) => {
    set({ loading: true, error: null })
    try {
      const [markowitz, riskParity, frontier] = await Promise.all([
        optimizeMarkowitz(tickers),
        optimizeRiskParity(tickers),
        getEfficientFrontier(tickers),
      ])
      set({ markowitz, riskParity, frontier, tickers, loading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Optimization failed'
      set({ loading: false, error: msg })
    }
  },

  runFrontier: async (tickers) => {
    set({ loading: true, error: null })
    try {
      const frontier = await getEfficientFrontier(tickers)
      set({ frontier, loading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Frontier calculation failed'
      set({ loading: false, error: msg })
    }
  },

  runRebalance: async (holdings, weights) => {
    set({ loading: true, error: null })
    try {
      const rebalance = await getRebalanceTrades(holdings, weights)
      set({ rebalance, loading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Rebalance failed'
      set({ loading: false, error: msg })
    }
  },

  fetchComparison: async () => {
    set({ loading: true, error: null })
    try {
      const comparison = await getCurrentVsOptimal()
      set({ comparison, loading: false })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Comparison failed'
      set({ loading: false, error: msg })
    }
  },

  clear: () => set({
    markowitz: null, riskParity: null, frontier: null,
    rebalance: null, comparison: null, error: null,
  }),
}))
